/**
 * StationTable component
 * Displays production metrics grouped by station
 * With dark mode support
 */
import React, { useState } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { StationSummary } from '../../../../services/productionService';
import { formatActiveHours } from '../../../../utils/dateUtils';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface StationTableProps {
  stations: StationSummary[];
}

type SortField = 'station_name' | 'location_name' | 'sessions_count' | 'boxes_packed' | 'total_weight' | 'active_hours';
type SortDirection = 'asc' | 'desc';

const StationTable: React.FC<StationTableProps> = ({ stations }) => {
  const { t } = useLanguage();
  const [sortField, setSortField] = useState<SortField>('total_weight');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedStations = [...stations].sort((a, b) => {
    let aVal = a[sortField] ?? '';
    let bVal = b[sortField] ?? '';

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal as string).toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  if (stations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t('endOfDaySummary.noData')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900/50">
          <tr>
            <th
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('station_name')}
            >
              {t('endOfDaySummary.stationName')}
              <SortIcon field="station_name" />
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('location_name')}
            >
              {t('endOfDaySummary.location')}
              <SortIcon field="location_name" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('sessions_count')}
            >
              {t('endOfDaySummary.sessionsCount')}
              <SortIcon field="sessions_count" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('boxes_packed')}
            >
              {t('endOfDaySummary.boxesPacked')}
              <SortIcon field="boxes_packed" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('total_weight')}
            >
              {t('endOfDaySummary.weight')}
              <SortIcon field="total_weight" />
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('active_hours')}
            >
              {t('endOfDaySummary.activeHours')}
              <SortIcon field="active_hours" />
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedStations.map((station, index) => (
            <tr key={station.station_id} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/30'}>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                {station.station_name}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {station.location_name || '-'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                {station.sessions_count}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                {station.boxes_packed.toLocaleString()}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                {station.total_weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                {formatActiveHours(station.active_hours)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StationTable;
